/* eslint-disable react/no-array-index-key */
import React, { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { useDispatch, useSelector } from 'react-redux'
import DatePickerComponent from 'src/components/DatePicker'
import { Loader } from 'src/components'
import StatisticsComponent from 'src/agency/components/StatisticsComponent'
import * as adminSelectors from '../selectors'
import colors from '../../static/colors'
import { getInterviews, getAgencies } from '../actions'
import FilterComponent from './FilterComponent'
import DashboardTableComponent from './DashboardTableComponent'
import DashboardFunnelComponent from './DashboardFunnelComponent'

const Container = styled.div`
  position: relative;
`

const Title = styled.h2`
  color: #222b45;
  font-size: 26px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 32px;
  margin: 0 0 20px;
  color: ${colors.title};
  white-space: pre-line;

  @media screen and (max-width: 600px) {
    padding-bottom: 10px;
  }
`

const TabsContainer = styled.div`
  display: flex;
  margin: 40px 0;
`

const TabContainer = styled.div<{ active?: boolean }>`
  width: 120px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-weight: ${({ active }) => (active ? 500 : 400)};
  border-bottom: 3px solid ${({ active }) => (active ? 'black' : 'transparent')};
`

const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  margin: 0 0 20px;
`

const DatePickerWrapper = styled.div`
  width: 300px;
  margin-right: 12px;
  > div {
    > div {
      > div {
        > div {
          width: 100%;
        }
      }
    }
  }
`

const LinkButton = styled.div`
  cursor: pointer;
  font-size: 12px;
  color: #b202ff;
  text-decoration: underline;
  margin: 0 20px 0 0;
  height: 56px;
  display: flex;
  align-items: center;
`

enum Tab {
  Statistics = 'Statistics',
  Table = 'Table',
  Funnel = 'Funnel',
}

const Tabs = Object.values(Tab)

const DashboardComponent = () => {
  const dispatch = useDispatch()
  const [activeTab, setActiveTab] = useState(Tab.Table)
  const [fromDate, setFromDate] = useState(new Date())
  const [toDate, setToDate] = useState(new Date())
  const [selectedAgencies, setSelectedAgencies] = useState([])
  const [isAgencyFilterVisible, setIsAgencyFilterVisible] = useState(false)
  const interviews = useSelector(adminSelectors.interviews.interviews)
  const isLoading = useSelector(adminSelectors.interviews.isLoading)
  const agencies = useSelector(adminSelectors.agencies.agencies)

  const renderDataComponent = (tab: Tab) => {
    switch (tab) {
      case Tab.Table:
        return <DashboardTableComponent interviews={interviews} />
      case Tab.Funnel:
        return <DashboardFunnelComponent interviews={interviews} />
      case Tab.Statistics:
        return <StatisticsComponent />
      default:
        return null
    }
  }

  useEffect(() => {
    dispatch(
      getInterviews({
        page: 1,
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedAgencies])

  useEffect(() => {
    dispatch(getAgencies())
  }, [dispatch])

  return (
    <>
      <Container>
        <Title>Dashboard</Title>
        <TabsContainer>
          {Tabs.map((t) => (
            <TabContainer
              key={t}
              active={activeTab === t}
              onClick={() => setActiveTab(t)}
            >
              {t}
            </TabContainer>
          ))}
        </TabsContainer>
        <FilterContainer>
          <LinkButton onClick={() => setIsAgencyFilterVisible(true)}>
            Agency Filter
          </LinkButton>
          <DatePickerWrapper>
            <DatePickerComponent
              value={fromDate}
              label="From date"
              shouldCloseOnSelect
              onChange={setFromDate}
              disableFuture
            />
          </DatePickerWrapper>
          <DatePickerWrapper>
            <DatePickerComponent
              value={toDate}
              label="To date"
              shouldCloseOnSelect
              onChange={setToDate}
              disableFuture
            />
          </DatePickerWrapper>
        </FilterContainer>
        {isLoading ? <Loader /> : renderDataComponent(activeTab)}
      </Container>
      {isAgencyFilterVisible && (
        <FilterComponent
          data={agencies.map((a) => ({
            label: a.internal_name || a.name,
            id: a.id,
          }))}
          excludedData={[]}
          onChange={setSelectedAgencies}
          onClose={() => setIsAgencyFilterVisible(false)}
        />
      )}
    </>
  )
}

export default DashboardComponent
